"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";

const wcagLevels = [
  {
    level: "A",
    description: "Minimum level - Essential accessibility features",
    examples: ["Text alternatives for images", "Keyboard accessible", "No seizure-inducing content"],
  },
  {
    level: "AA",
    description: "Mid-range level - Our target compliance",
    examples: ["4.5:1 contrast ratio for text", "Resize text up to 200%", "Multiple ways to find content"],
  },
  {
    level: "AAA",
    description: "Highest level - Enhanced accessibility",
    examples: ["7:1 contrast ratio", "Sign language for video", "Extended audio descriptions"],
  },
];

const principles = [
  {
    title: "Perceivable",
    description: "Information must be presentable in ways users can perceive",
    guidelines: [
      "Provide text alternatives for non-text content",
      "Provide captions and alternatives for multimedia",
      "Create content that can be presented in different ways",
      "Make it easier for users to see and hear content",
    ],
  },
  {
    title: "Operable",
    description: "User interface components must be operable",
    guidelines: [
      "Make all functionality available from a keyboard",
      "Give users enough time to read and use content",
      "Do not design content that causes seizures",
      "Help users navigate and find content",
    ],
  },
  {
    title: "Understandable",
    description: "Information and UI operation must be understandable",
    guidelines: [
      "Make text readable and understandable",
      "Make content appear and operate in predictable ways",
      "Help users avoid and correct mistakes",
    ],
  },
  {
    title: "Robust",
    description: "Content must be robust enough for various technologies",
    guidelines: [
      "Maximize compatibility with current and future tools",
      "Use valid, semantic HTML",
      "Ensure ARIA attributes are used correctly",
    ],
  },
];

const checklist = [
  { category: "Keyboard", items: ["Tab navigation works", "Focus visible", "No keyboard traps", "Logical tab order"] },
  { category: "Screen Readers", items: ["Proper headings hierarchy", "ARIA labels on controls", "Live regions for updates", "Form labels associated"] },
  { category: "Visual", items: ["4.5:1 contrast ratio", "Focus indicators visible", "Not color-only indicators", "Resizable text"] },
  { category: "Motion", items: ["Respect prefers-reduced-motion", "Pause/stop animations", "No auto-playing media", "No flashing content"] },
];

export default function DocsAccessibility() {
  return (
    <>
      {/* Page Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Accessibility
            </Text>
            <Badge variant="secondary">WCAG 2.1 AA</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            Building accessible interfaces is not optional. Our components are designed
            to meet WCAG 2.1 AA standards, ensuring everyone can use our products.
          </Text>
        </div>
      </Section>

      {/* WCAG Levels */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>WCAG Compliance Levels</CardTitle>
              <CardDescription>Web Content Accessibility Guidelines conformance</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {wcagLevels.map((level) => (
              <div key={level.level} className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Badge variant={level.level === "AA" ? "default" : "outline"}>
                    Level {level.level}
                  </Badge>
                  {level.level === "AA" && (
                    <Text as="p" className="text-xs text-primary">Our target</Text>
                  )}
                </div>
                <Text as="p" className="text-sm text-muted-foreground">
                  {level.description}
                </Text>
                <ul className="space-y-1 text-sm">
                  {level.examples.map((example, i) => (
                    <li key={i} className="flex gap-2">
                      <div className="text-primary">-</div>
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* POUR Principles */}
      <Section title="POUR Principles">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {principles.map((principle) => (
            <SectionCard key={principle.title} contentClassName="p-6">
              <div className="flex flex-col gap-3">
                <Text as="p" className="font-semibold">{principle.title}</Text>
                <Text as="p" className="text-sm text-muted-foreground">
                  {principle.description}
                </Text>
                <ul className="space-y-2 text-sm">
                  {principle.guidelines.map((guideline, i) => (
                    <li key={i} className="flex gap-2">
                      <div className="text-primary">-</div>
                      {guideline}
                    </li>
                  ))}
                </ul>
              </div>
            </SectionCard>
          ))}
        </div>
      </Section>

      {/* Component Checklist */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Accessibility Checklist</CardTitle>
              <CardDescription>Use this checklist when building or reviewing components</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {checklist.map((section) => (
              <div key={section.category} className="flex flex-col gap-3">
                <Text as="p" className="font-semibold">{section.category}</Text>
                <ul className="space-y-2 text-sm">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded border border-input flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* Testing Tools */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Testing Tools</CardTitle>
              <CardDescription>Recommended tools for accessibility testing</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex flex-col gap-1 p-3 bg-muted/30 rounded-lg">
              <Text as="p" className="font-medium">axe DevTools</Text>
              <Text as="p" className="text-muted-foreground">Browser extension for automated testing</Text>
            </div>
            <div className="flex flex-col gap-1 p-3 bg-muted/30 rounded-lg">
              <Text as="p" className="font-medium">WAVE</Text>
              <Text as="p" className="text-muted-foreground">Web accessibility evaluation tool</Text>
            </div>
            <div className="flex flex-col gap-1 p-3 bg-muted/30 rounded-lg">
              <Text as="p" className="font-medium">VoiceOver / NVDA</Text>
              <Text as="p" className="text-muted-foreground">Screen reader testing</Text>
            </div>
            <div className="flex flex-col gap-1 p-3 bg-muted/30 rounded-lg">
              <Text as="p" className="font-medium">Keyboard Only</Text>
              <Text as="p" className="text-muted-foreground">Navigate without mouse</Text>
            </div>
          </div>
        </SectionCard>
      </Section>

      {/* Code Examples */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Best Practices</CardTitle>
              <CardDescription>Common patterns for accessible components</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-6"
        >
          <div className="space-y-3">
            <Text as="p" className="font-medium">Form Labels</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
              <code>{`<label htmlFor="email">Email</label>
<input id="email" type="email" aria-describedby="email-hint" />
<div id="email-hint">We'll never share your email</div>`}</code>
            </div>
          </div>

          <div className="space-y-3">
            <Text as="p" className="font-medium">Button with Icon</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
              <code>{`<button aria-label="Close dialog">
  <XIcon aria-hidden="true" />
</button>`}</code>
            </div>
          </div>

          <div className="space-y-3">
            <Text as="p" className="font-medium">Live Regions</Text>
            <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
              <code>{`<div role="alert" aria-live="polite">
  Form submitted successfully
</div>`}</code>
            </div>
          </div>
        </SectionCard>
      </Section>
    </>
  );
}
